import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Logo } from "../components/Logo";
import { permitMeta } from "../lib/permitSchemas";
import { Loader2, BadgeCheck, CalendarX, ShieldAlert } from "lucide-react";

const fmt = (dt) => { if (!dt) return "—"; try { return new Date(dt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return dt; } };

export default function PublicVerify() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/public/contractors/${id}`).then((r) => setC(r.data)).catch(() => setErr(true));
  }, [id]);

  if (err) return <Center><ShieldAlert className="mb-3 h-8 w-8 text-destructive" /><p className="text-muted-foreground">Contractor not found.</p></Center>;
  if (!c) return <Center><Loader2 className="h-6 w-6 animate-spin text-brand" /></Center>;

  return (
    <div className="min-h-screen bg-background">
      <div className="flex items-center justify-center border-b border-border bg-white px-4 py-3"><Logo /></div>
      <div className="mx-auto max-w-md px-4 py-6">
        <div className="overflow-hidden rounded-xl border border-border bg-white shadow-sm">
          <div className="flex items-center gap-4 p-5" style={{ background: "#00355e" }}>
            <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-md border-2 border-white/40 bg-white/10">
              {c.contractor_photo ? <img src={c.contractor_photo} alt={c.name} className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-xs text-white/60">No photo</div>}
            </div>
            <div className="min-w-0 text-white">
              <div className="font-heading text-2xl font-bold leading-tight">{c.name}</div>
              <div className="text-sm text-white/80">{c.company}</div>
              <div className="mt-1 text-xs text-white/60">DBS: {c.dbs_number || "—"} · {c.reference}</div>
            </div>
          </div>

          <Section title="Site inductions">
            {c.inductions.length === 0 ? <Empty text="No site inductions." /> : c.inductions.map((i, idx) => (
              <div key={idx} className="flex items-center justify-between border-b border-border py-2 last:border-0" data-testid={`verify-induction-${idx}`}>
                <div className="text-sm font-medium">{i.site}</div>
                {i.active ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700"><BadgeCheck className="h-3 w-3" /> to {fmt(i.induction_expiry)}</span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700"><CalendarX className="h-3 w-3" /> expired</span>
                )}
              </div>
            ))}
          </Section>

          <Section title="Permits">
            {c.permits.length === 0 ? <Empty text="No permits issued." /> : c.permits.map((p, idx) => (
              <div key={idx} className="flex items-center justify-between border-b border-border py-2 last:border-0" data-testid={`verify-permit-${idx}`}>
                <div>
                  <div className="text-sm font-semibold text-brand">{p.permit_number}</div>
                  <div className="flex flex-wrap gap-1 pt-1">
                    {(p.permit_types || []).map((t) => <span key={t} className="rounded px-1.5 py-0.5 text-[10px] font-semibold text-white" style={{ background: permitMeta(t)?.color }}>{permitMeta(t)?.short}</span>)}
                  </div>
                  <div className="text-xs text-muted-foreground">{p.site_location}</div>
                </div>
                {p.active ? <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">Active</span> : <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-semibold text-neutral-500">Expired</span>}
              </div>
            ))}
          </Section>

          <div className="px-5 py-3 text-xs text-muted-foreground">Emergency contact: {c.emergency_contact || "—"}</div>
        </div>
        <p className="mt-4 text-center text-xs text-muted-foreground">Scanned from a GFM permit badge · read‑only</p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="px-5 py-3">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{title}</div>
      {children}
    </div>
  );
}
function Empty({ text }) { return <div className="py-2 text-sm text-muted-foreground">{text}</div>; }
function Center({ children }) { return <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">{children}</div>; }
